import React, { useEffect, useRef, useState } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Divider, Flex, IconButton,
  FormControl, FormLabel, Input, Textarea, Select, Button, Spinner, Stack, useToast,
} from '@chakra-ui/react';
import {
  FiSettings, FiX, FiTrash2, FiChevronUp, FiChevronDown, FiPlus, FiUpload,
} from 'react-icons/fi';
import { LuPin } from 'react-icons/lu';
import {
  metricTreeApi, AGG_OPTIONS, AGG_NEEDS_PROPERTY,
  NODE_KIND_OPTIONS, OPERATOR_OPTIONS, OP_LABEL, NOTE_COLORS,
} from './metricTreeApi';
import { computeBlockedInputIds } from './formulaEval';

const TYPE_LABEL = { metric: 'Metric', strategy: 'Strategy', note: 'Note', text: 'Text', media: 'Media' };

const currentUser = () => {
  try {
    const p = JSON.parse(atob(localStorage.getItem('customer-portal').split('.')[1]));
    return p?.userdata?.username || p?.userdata?.email || 'You';
  } catch (_) { return 'You'; }
};
const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_) { return ''; } };

/**
 * MetricNodePanel — configures the selected node's metric (label, event, agg,
 * property). Mirrors FlowBuilder/PropertyPanel.jsx layout. Property is shown and
 * required only when the aggregation is sum/avg.
 *
 * Events + attributes are fetched from the SAME endpoints Boards' TileEditor uses
 * (/fetch-unique-events, /fetch-unique-attributes) via metricTreeApi. If the event
 * list can't load, the event field degrades to a free-typed input with a datalist.
 */
export default function MetricNodePanel({
  selectedNode,
  onChange, // (patch) => void — patch merged into node.data
  onDelete, // () => void
  onClose,
  events,
  eventsError,
  eventsLoading,
  allNodes = [], // [{ id, label }] — candidate inputs for a formula node
  readOnly = false, // MT-02: view mode — inspect config but no edits/delete
}) {
  const data = selectedNode?.data || {};
  const metric = data.metric || {};
  // A metric can aggregate over ONE event or a UNION of several (metric.events[]).
  // Normalise both shapes to a single list the editor works with.
  const selectedEvents = Array.isArray(metric.events) && metric.events.length
    ? metric.events
    : (metric.event ? [metric.event] : []);
  const primaryEvent = selectedEvents[0] || null;
  const kind = data.kind === 'formula' ? 'formula' : 'metric';
  const formula = data.formula || { operator: '+', inputs: [] };
  const type = data.type || 'metric';
  const annotation = data.annotation || {};
  const patchAnnotation = (partial) => onChange({ annotation: { ...annotation, ...partial } });

  const [attributes, setAttributes] = useState([]);
  const [attrsLoading, setAttrsLoading] = useState(false);
  const [draft, setDraft] = useState('');

  // Media-node image upload (BUG-MT-08): pick a file → validate → upload to the
  // shared media bucket → store the hosted URL on the annotation.
  const toast = useToast();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const onPickImage = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
      toast({ title: 'Unsupported file type', description: 'Upload a JPG, PNG, GIF, or WEBP image.', status: 'error', duration: 4000, isClosable: true });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Image too large', description: `Images must be under 5MB (this file is ${(file.size / (1024 * 1024)).toFixed(1)}MB).`, status: 'error', duration: 4000, isClosable: true });
      return;
    }
    setUploading(true);
    try {
      const url = await metricTreeApi.uploadImage(file);
      patchAnnotation({ url });
      toast({ title: 'Image uploaded', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.message || err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setUploading(false);
    }
  };

  const comments = Array.isArray(data.comments) ? data.comments : [];
  const addComment = () => {
    const text = draft.trim();
    if (!text) return;
    onChange({ comments: [...comments, { text, author: currentUser(), at: new Date().toISOString() }] });
    setDraft('');
  };

  const needsProperty = AGG_NEEDS_PROPERTY.has(metric.agg);

  const patchFormula = (partial) => onChange({ formula: { ...formula, ...partial } });
  const inputs = Array.isArray(formula.inputs) ? formula.inputs : [];
  const op = formula.operator || '+';
  const glyph = OP_LABEL[op] || op;

  // Nodes that would create a circular reference if used as an input to THIS
  // formula node (itself + anything that already depends on it) — BUG-MT-05.
  // allNodes carries { id, kind, inputs } from the canvas so this stays a pure
  // graph walk with no extra fetch.
  const blockedInputIds = React.useMemo(() => {
    const graph = allNodes.map((n) => ({
      id: n.id,
      data: { kind: n.kind === 'formula' ? 'formula' : 'metric', formula: { inputs: n.inputs || [] } },
    }));
    return computeBlockedInputIds(graph, selectedNode?.id);
  }, [allNodes, selectedNode?.id]);

  // Only metric/formula nodes (things with a computable value) can be inputs —
  // never the node itself, never text/note/media/strategy annotations, and never a
  // node whose selection would close a formula cycle.
  const metricCandidates = allNodes.filter(
    (n) => n.id !== selectedNode?.id && (n.type || 'metric') === 'metric' && !blockedInputIds.has(n.id),
  );
  // MT-12: when two nodes share a label they're indistinguishable in the picker,
  // which lets a duplicate silently feed the metric it duplicates. Append a short
  // id suffix whenever a label collides so every option/preview entry is unique.
  const labelCounts = {};
  for (const n of allNodes) { const l = (n.label || '').trim(); if (l) labelCounts[l] = (labelCounts[l] || 0) + 1; }
  const labelOf = (id) => {
    const n = allNodes.find((x) => x.id === id);
    if (!n) return id;
    const l = n.label || id;
    return labelCounts[(n.label || '').trim()] > 1 ? `${l} · #${String(id).slice(-4)}` : l;
  };
  const available = metricCandidates.filter((n) => !inputs.includes(n.id));

  // Guard: refuse duplicates (BUG-MT-04) and cycle-creating inputs (BUG-MT-05)
  // even if a stale id slips through the filtered list.
  const addInput = (id) => {
    if (inputs.includes(id) || blockedInputIds.has(id)) return;
    patchFormula({ inputs: [...inputs, id] });
  };
  const removeInput = (id) => patchFormula({ inputs: inputs.filter((x) => x !== id) });
  const moveInput = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= inputs.length) return;
    const next = [...inputs];
    [next[i], next[j]] = [next[j], next[i]];
    patchFormula({ inputs: next });
  };
  // Live formula preview, e.g. "Total Inflows − Total Outflows" (order matters for − ÷).
  const preview = inputs.length ? inputs.map(labelOf).join(` ${glyph} `) : '';

  // Load numeric attributes when the event changes and the agg needs a property.
  useEffect(() => {
    let alive = true;
    if (!primaryEvent || !needsProperty) {
      setAttributes([]);
      return undefined;
    }
    setAttrsLoading(true);
    metricTreeApi
      .listEventAttributes(primaryEvent)
      .then((r) => {
        if (!alive) return;
        setAttributes(Array.isArray(r.data) ? r.data : []);
      })
      .finally(() => alive && setAttrsLoading(false));
    return () => {
      alive = false;
    };
  }, [primaryEvent, needsProperty]);

  const patchMetric = (partial) => onChange({ metric: { ...metric, ...partial } });
  // Persist the event list: a single event stays in the classic `event` field
  // (backward compatible); two or more become a `events[]` union.
  const setEvents = (list) => {
    const uniq = [...new Set((list || []).filter(Boolean))];
    if (uniq.length <= 1) patchMetric({ event: uniq[0] || null, events: undefined });
    else patchMetric({ event: null, events: uniq });
  };
  const addEvent = (name) => { if (name) setEvents([...selectedEvents, name]); };
  const removeEvent = (name) => setEvents(selectedEvents.filter((e) => e !== name));

  if (!selectedNode) {
    return (
      <Box w="360px" bg="white" borderLeft="1px" borderColor="gray.200" flexShrink={0}>
        <VStack spacing={3} align="center" justify="center" h="300px" textAlign="center" px={4}>
          <FiSettings size={48} color="#CBD5E0" />
          <Text fontSize="sm" color="gray.600">Select a metric node to configure it</Text>
          <Text fontSize="xs" color="gray.500">Click a node on the canvas, or add a new one from the toolbar</Text>
        </VStack>
      </Box>
    );
  }

  const eventOptions = Array.isArray(events) ? events : [];
  const useFreeEventInput = eventsError || eventOptions.length === 0;

  return (
    <Box w="360px" bg="white" borderLeft="1px" borderColor="gray.200" overflowY="auto" flexShrink={0}>
      <Box p={4}>
        <Flex align="flex-start" justify="space-between" mb={2}>
          <Box flex={1}>
            <Heading size="sm" mb={1} fontFamily="Open Sans, sans-serif">{TYPE_LABEL[type] || 'Node'} properties</Heading>
            <Text fontSize="xs" color="gray.600">
              {readOnly
                ? `Viewing this ${(TYPE_LABEL[type] || 'node').toLowerCase()} node`
                : `Configure this ${(TYPE_LABEL[type] || 'node').toLowerCase()} node`}
            </Text>
          </Box>
          <IconButton
            size="xs" variant="ghost" icon={<FiX />} onClick={onClose}
            aria-label="Close panel" color="gray.500"
            _hover={{ color: 'gray.800', bg: 'gray.100' }}
          />
        </Flex>
        <Divider mb={4} />
        {readOnly && (
          <Box mb={4} bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="md" px={3} py={2}>
            <Text fontSize="xs" color="gray.600">
              View only — switch to <b>Edit</b> to change this node.
            </Text>
          </Box>
        )}
        <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0, minInlineSize: 'auto' }}>

        <VStack spacing={4} align="stretch">
          {/* BUG-MT-17: a Text node's authoritative title is its Heading (also
              bound to data.label, rendered below). Showing the generic Label here
              too surfaced two inputs holding the identical value — hide it for
              Text so Heading is the single source. */}
          {type !== 'text' && (
          <FormControl>
            <FormLabel fontSize="sm">Label</FormLabel>
            <Input
              size="sm"
              placeholder="e.g. Bill payments"
              value={data.label || ''}
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </FormControl>
          )}

          {/* MT-03: Owner is only meaningful for business entities (Metric,
              Strategy) — omit it for Text / Note / Media. */}
          {(type === 'metric' || type === 'strategy') && (
          <FormControl>
            <FormLabel fontSize="sm">Owner</FormLabel>
            <Input
              size="sm"
              placeholder="Who owns this KPI (name or email)"
              value={data.owner || ''}
              onChange={(e) => onChange({ owner: e.target.value })}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>Shown as an avatar on the card.</Text>
          </FormControl>
          )}

          {/* ── Metric node config (mode + event/formula) ── */}
          {type === 'metric' && (
          <>
          <FormControl>
            <FormLabel fontSize="sm">Metric mode</FormLabel>
            <Select
              size="sm"
              value={kind}
              onChange={(e) => {
                const k = e.target.value;
                // Seed a default formula when switching to a derived node.
                onChange(k === 'formula'
                  ? { kind: 'formula', formula: { operator: formula.operator || '+', inputs: formula.inputs || [] } }
                  : { kind: 'metric' });
              }}
            >
              {NODE_KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
            <Text fontSize="xs" color="gray.500" mt={1}>
              {kind === 'formula'
                ? 'Value is derived from other nodes via an operator.'
                : 'Value is an aggregate of an event off your data.'}
            </Text>
          </FormControl>

          {/* ── Formula node: operator + inputs ── */}
          {kind === 'formula' && (
            <>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Operator</FormLabel>
                <Select
                  size="sm"
                  value={op}
                  onChange={(e) => patchFormula({ operator: e.target.value })}
                >
                  {OPERATOR_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {(op === '-' || op === '/')
                    ? `Order matters — folds left: input 1 ${glyph} input 2 ${glyph} …`
                    : 'Order does not matter for this operator.'}
                </Text>
              </FormControl>

              {/* Selected inputs, in order (drives which value subtracts from which) */}
              <FormControl isRequired>
                <FormLabel fontSize="sm">Inputs (in order)</FormLabel>
                {inputs.length === 0 ? (
                  <Text fontSize="xs" color="orange.500">
                    Add at least one metric from the list below.
                  </Text>
                ) : (
                  <Stack spacing={1}>
                    {inputs.map((id, i) => (
                      <Flex key={id} align="center" gap={1}
                        borderWidth="1px" borderColor="gray.200" borderRadius="md" px={2} py={1}
                      >
                        <Text fontSize="xs" color="gray.400" w="16px">{i + 1}.</Text>
                        <Text fontSize="sm" flex={1} noOfLines={1}>{labelOf(id)}</Text>
                        <IconButton aria-label="Move up" size="xs" variant="ghost" icon={<FiChevronUp />}
                          isDisabled={i === 0} onClick={() => moveInput(i, -1)} />
                        <IconButton aria-label="Move down" size="xs" variant="ghost" icon={<FiChevronDown />}
                          isDisabled={i === inputs.length - 1} onClick={() => moveInput(i, 1)} />
                        <IconButton aria-label="Remove" size="xs" variant="ghost" colorScheme="red" icon={<FiX />}
                          onClick={() => removeInput(id)} />
                      </Flex>
                    ))}
                  </Stack>
                )}
              </FormControl>

              {/* Live preview of the derived formula */}
              {inputs.length > 0 && (
                <Box bg="gray.50" borderRadius="md" px={3} py={2}>
                  <Text fontSize="xs" color="gray.500" mb={1}>This node computes</Text>
                  <Text fontSize="sm" fontWeight={700} color="gray.800" wordBreak="break-word">
                    {preview}
                  </Text>
                </Box>
              )}

              {/* Add metrics */}
              {available.length > 0 && (
                <FormControl>
                  <FormLabel fontSize="sm">Add a metric</FormLabel>
                  <Stack spacing={1} maxH="160px" overflowY="auto" pr={1}>
                    {available.map((n) => (
                      <Button key={n.id} size="xs" variant="outline" justifyContent="flex-start"
                        leftIcon={<FiPlus />} onClick={() => addInput(n.id)}
                      >
                        <Text fontSize="sm" noOfLines={1}>{n.label || n.id}</Text>
                      </Button>
                    ))}
                  </Stack>
                </FormControl>
              )}
              {metricCandidates.length === 0 && (
                <Text fontSize="xs" color="orange.500">Add other metric nodes first, then reference them here.</Text>
              )}
            </>
          )}

          {/* ── Metric node: event / aggregation / property ── */}
          {kind === 'metric' && (
          <>
          <FormControl isRequired>
            <FormLabel fontSize="sm">
              Event{selectedEvents.length > 1 ? 's (union)' : ''} {eventsLoading && <Spinner size="xs" ml={1} />}
            </FormLabel>

            {/* Selected event(s) as removable chips. Two or more = a union metric
                (e.g. active customers across several transacting events). */}
            {selectedEvents.length > 0 && (
              <HStack spacing={1} wrap="wrap" mb={2}>
                {selectedEvents.map((ev) => {
                  const label = eventOptions.find((o) => o.name === ev)?.display_name || ev;
                  return (
                    <HStack
                      key={ev} spacing={1} pl={2} pr={1} py={0.5}
                      bg="#EDEBFB" color="#4f5ed1" borderRadius="md" fontSize="xs" fontWeight={600}
                    >
                      <Text>{label}</Text>
                      <IconButton
                        aria-label={`Remove ${ev}`} icon={<FiX />} size="2xs" variant="ghost"
                        color="#4f5ed1" minW={4} h={4} onClick={() => removeEvent(ev)}
                      />
                    </HStack>
                  );
                })}
              </HStack>
            )}

            {/* Add another event */}
            {useFreeEventInput ? (
              <>
                <Input
                  size="sm"
                  list="metric-tree-events"
                  placeholder={selectedEvents.length ? 'Add another event…' : 'Type an event name'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) { addEvent(e.target.value.trim()); e.target.value = ''; e.preventDefault(); }
                  }}
                  onBlur={(e) => { if (e.target.value.trim()) { addEvent(e.target.value.trim()); e.target.value = ''; } }}
                />
                <datalist id="metric-tree-events">
                  {eventOptions.map((ev) => (
                    <option key={ev.name} value={ev.name}>
                      {ev.display_name || ev.name}
                    </option>
                  ))}
                </datalist>
                {eventsError && (
                  <Text fontSize="xs" color="orange.500" mt={1}>
                    Couldn't load the event list — type the event name and press Enter.
                  </Text>
                )}
              </>
            ) : (
              <Select
                size="sm"
                placeholder={selectedEvents.length ? '+ Add another event' : 'Select an event'}
                value=""
                onChange={(e) => { addEvent(e.target.value); }}
              >
                {eventOptions
                  .filter((ev) => !selectedEvents.includes(ev.name))
                  .map((ev) => (
                    <option key={ev.name} value={ev.name}>
                      {ev.display_name || ev.name}
                    </option>
                  ))}
              </Select>
            )}
            {selectedEvents.length > 1 && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                Counts each customer once across all {selectedEvents.length} events (union), not per event.
              </Text>
            )}
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Aggregation</FormLabel>
            <Select
              size="sm"
              value={metric.agg || 'count'}
              onChange={(e) => {
                const agg = e.target.value;
                // Clear property when switching to an agg that doesn't use one.
                patchMetric(AGG_NEEDS_PROPERTY.has(agg) ? { agg } : { agg, property: undefined });
              }}
            >
              {AGG_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </FormControl>

          {needsProperty && (
            <FormControl isRequired>
              <FormLabel fontSize="sm">
                Property {attrsLoading && <Spinner size="xs" ml={1} />}
              </FormLabel>
              {attributes.length > 0 ? (
                <Select
                  size="sm"
                  placeholder="Select a property"
                  value={metric.property || ''}
                  onChange={(e) => patchMetric({ property: e.target.value })}
                >
                  {metric.property && !attributes.some((a) => a.name === metric.property) && (
                    <option value={metric.property}>{metric.property}</option>
                  )}
                  {attributes.map((a) => (
                    <option key={a.name} value={a.name}>{a.name}</option>
                  ))}
                </Select>
              ) : (
                <Input
                  size="sm"
                  placeholder="Numeric property name (e.g. amount)"
                  value={metric.property || ''}
                  onChange={(e) => patchMetric({ property: e.target.value })}
                />
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                Required for Sum and Average.
              </Text>
            </FormControl>
          )}
          </>
          )}
          </>
          )}

          {/* ── Strategy node ── */}
          {type === 'strategy' && (
            <>
            {/* MT-07: Description binds to annotation.text — the field the card
                renders. Without it the card showed an inert "Write about…" placeholder. */}
            <FormControl>
              <FormLabel fontSize="sm">Description (optional)</FormLabel>
              <Textarea
                size="sm"
                rows={3}
                placeholder="What is this bet / strategy about?"
                value={annotation.text || ''}
                onChange={(e) => patchAnnotation({ text: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Target (optional)</FormLabel>
              <Input
                size="sm"
                placeholder="e.g. 2.2T"
                value={annotation.target || ''}
                onChange={(e) => patchAnnotation({ target: e.target.value })}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                The goal your metrics roll up to. Connect it to a metric node on the canvas.
              </Text>
            </FormControl>
            </>
          )}

          {/* ── Note node ── */}
          {type === 'note' && (
            <>
              <FormControl>
                <FormLabel fontSize="sm">Note text</FormLabel>
                <Textarea
                  size="sm"
                  rows={4}
                  placeholder="Write a note…"
                  value={annotation.text || ''}
                  onChange={(e) => patchAnnotation({ text: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Colour</FormLabel>
                <HStack spacing={2}>
                  {NOTE_COLORS.map((c) => (
                    <Box
                      key={c}
                      as="button"
                      w="24px"
                      h="24px"
                      borderRadius="6px"
                      bg={c}
                      borderWidth="2px"
                      borderColor={(annotation.color || NOTE_COLORS[0]) === c ? 'gray.600' : 'transparent'}
                      onClick={() => patchAnnotation({ color: c })}
                      aria-label={`note colour ${c}`}
                    />
                  ))}
                </HStack>
              </FormControl>
            </>
          )}

          {/* ── Text node ── MT-06: a heading + a separate multi-line body,
              rendered with distinct type styles on the card (the card reads
              data.label for the heading and annotation.description for the body). */}
          {type === 'text' && (
            <>
              <FormControl>
                <FormLabel fontSize="sm">Heading</FormLabel>
                <Input
                  size="sm"
                  placeholder="Heading"
                  value={data.label || ''}
                  onChange={(e) => onChange({ label: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Body (optional)</FormLabel>
                <Textarea
                  size="sm"
                  rows={3}
                  placeholder="Paragraph text…"
                  value={annotation.description || ''}
                  onChange={(e) => patchAnnotation({ description: e.target.value })}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Shown under the heading on the canvas.
                </Text>
              </FormControl>
            </>
          )}

          {/* ── Media node ── */}
          {type === 'media' && (
            <>
              <FormControl>
                <FormLabel fontSize="sm">Image</FormLabel>
                <HStack spacing={2}>
                  <Button
                    size="sm" variant="outline"
                    leftIcon={uploading ? <Spinner size="xs" /> : <FiUpload />}
                    onClick={() => fileRef.current && fileRef.current.click()}
                    isDisabled={uploading}
                  >
                    {uploading ? 'Uploading…' : (annotation.url ? 'Replace image' : 'Upload image')}
                  </Button>
                  {annotation.url && !uploading && (
                    <Text fontSize="xs" color="green.600">Image set</Text>
                  )}
                </HStack>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={onPickImage}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  JPG, PNG, GIF or WEBP, up to 5MB — stored in your media library.
                </Text>
                {annotation.url && (
                  <Box mt={2} borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                    <img
                      src={annotation.url}
                      alt={annotation.caption || 'preview'}
                      style={{ width: '100%', maxHeight: 120, objectFit: 'cover', display: 'block' }}
                    />
                  </Box>
                )}
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">…or paste an image URL</FormLabel>
                <Input
                  size="sm"
                  placeholder="https://…"
                  value={annotation.url || ''}
                  onChange={(e) => patchAnnotation({ url: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Caption (optional)</FormLabel>
                <Input
                  size="sm"
                  placeholder="Caption"
                  value={annotation.caption || ''}
                  onChange={(e) => patchAnnotation({ caption: e.target.value })}
                />
              </FormControl>
            </>
          )}

          <Divider />

          {/* Pin */}
          <Button
            size="sm"
            variant={data.pinned ? 'solid' : 'outline'}
            colorScheme={data.pinned ? 'blue' : 'gray'}
            leftIcon={<LuPin />}
            onClick={() => onChange({ pinned: !data.pinned })}
          >
            {data.pinned ? 'Pinned — click to unpin' : 'Pin this KPI'}
          </Button>

          {/* Comments — MT-04: only Metric + Strategy carry a discussion thread;
              Text / Note / Media are presentational and omit it. */}
          {(type === 'metric' || type === 'strategy') && (
          <FormControl>
            <FormLabel fontSize="sm">Comments {comments.length ? `(${comments.length})` : ''}</FormLabel>
            {comments.length > 0 && (
              <Stack spacing={2} mb={2} maxH="220px" overflowY="auto">
                {comments.map((c, i) => (
                  <Box key={i} bg="gray.50" borderRadius="md" px={3} py={2}>
                    <Text fontSize="sm" whiteSpace="pre-wrap">{c.text}</Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>{c.author || 'You'} · {fmtWhen(c.at)}</Text>
                  </Box>
                ))}
              </Stack>
            )}
            <Textarea
              size="sm" rows={2} placeholder="Add a comment…"
              value={draft} onChange={(e) => setDraft(e.target.value)}
            />
            <Button size="sm" mt={2} colorScheme="blue" onClick={addComment} isDisabled={!draft.trim()}>
              Add comment
            </Button>
          </FormControl>
          )}

          {!readOnly && (
            <>
              <Divider />
              <Button
                size="sm" variant="outline" colorScheme="red"
                leftIcon={<FiTrash2 />} onClick={onDelete}
              >
                Delete node
              </Button>
            </>
          )}
        </VStack>
        </fieldset>
      </Box>
    </Box>
  );
}
